/**
 * expiry.ts — Pure computation of the expiry report: which stock batches have
 * expired or are about to, and how much money is at risk of a write-off.
 *
 * For a grocery or pharmacy this is loss prevention: an expired batch is a
 * direct write-off, and a batch expiring in days is a markdown-or-lose-it
 * decision. Each in-stock batch (quantity > 0, with an expiry date) is placed
 * in a window relative to `asOf`:
 *
 *  - **EXPIRED** — already past its expiry date.
 *  - **NEAR**    — expiring within `nearWindowDays` (default 30).
 *  - **SOON**    — expiring within `soonWindowDays` (default 90).
 *  - **LATER**   — beyond that; not at immediate risk.
 *
 * Value at risk is `quantity × unit cost` (the batch's own `cost_price` where it
 * has one, else the product's purchase price) — what the shop actually loses if
 * the stock is binned.
 *
 * Pure — see `expiryDataSource.ts` for the WatermelonDB bridge.
 */

export type ExpiryBucket = 'EXPIRED' | 'NEAR' | 'SOON' | 'LATER';

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

const DAY_MS = 24 * 60 * 60 * 1000;

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface ExpiryBatchInput {
  batchNumber: string;
  productId: string;
  productName: string;
  category: string;
  expiryDate: Date;
  quantity: number;
  /** Batch cost where known, else the product's purchase price. */
  unitCost: number;
}

export interface ExpiryInput {
  batches: ExpiryBatchInput[];
  asOf: Date;
  /** Upper bound (days) of the NEAR window. Default 30. */
  nearWindowDays?: number;
  /** Upper bound (days) of the SOON window. Default 90. */
  soonWindowDays?: number;
}

// ─── Output ──────────────────────────────────────────────────────────────────

export interface ExpiryItem {
  batchNumber: string;
  productId: string;
  productName: string;
  category: string;
  expiryDate: Date;
  /** Whole days until expiry; negative if already expired. */
  daysToExpiry: number;
  quantity: number;
  valueAtRisk: number;
  bucket: ExpiryBucket;
}

export interface BucketSummary {
  count: number;
  quantity: number;
  value: number;
}

export interface ExpiryReport {
  /** Batches sorted soonest-expiry first (most overdue at the top). */
  items: ExpiryItem[];
  expired: BucketSummary;
  near: BucketSummary;
  soon: BucketSummary;
  later: BucketSummary;
  /** Value in EXPIRED + NEAR + SOON — the money to act on. */
  totalAtRisk: number;
  nearWindowDays: number;
  soonWindowDays: number;
}

const BUCKET_RANK: Record<ExpiryBucket, number> = { EXPIRED: 0, NEAR: 1, SOON: 2, LATER: 3 };

export function buildExpiry(input: ExpiryInput): ExpiryReport {
  const { batches, asOf } = input;
  const nearWindowDays = input.nearWindowDays ?? 30;
  const soonWindowDays = input.soonWindowDays ?? 90;

  const items: ExpiryItem[] = batches
    .filter((b) => b.quantity > 0)
    .map((b) => {
      const daysToExpiry = Math.floor((b.expiryDate.getTime() - asOf.getTime()) / DAY_MS);
      let bucket: ExpiryBucket;
      if (daysToExpiry < 0) bucket = 'EXPIRED';
      else if (daysToExpiry <= nearWindowDays) bucket = 'NEAR';
      else if (daysToExpiry <= soonWindowDays) bucket = 'SOON';
      else bucket = 'LATER';
      return {
        batchNumber: b.batchNumber,
        productId: b.productId,
        productName: b.productName,
        category: b.category,
        expiryDate: b.expiryDate,
        daysToExpiry,
        quantity: b.quantity,
        valueAtRisk: round2(b.quantity * b.unitCost),
        bucket,
      };
    })
    // Soonest expiry first; the most overdue batch sits at the top.
    .sort((a, b) => a.expiryDate.getTime() - b.expiryDate.getTime());

  const summary = (bucket: ExpiryBucket): BucketSummary => {
    const rows = items.filter((i) => i.bucket === bucket);
    return {
      count: rows.length,
      quantity: round2(rows.reduce((s, i) => s + i.quantity, 0)),
      value: round2(rows.reduce((s, i) => s + i.valueAtRisk, 0)),
    };
  };

  const expired = summary('EXPIRED');
  const near = summary('NEAR');
  const soon = summary('SOON');
  const later = summary('LATER');

  return {
    items,
    expired,
    near,
    soon,
    later,
    totalAtRisk: round2(expired.value + near.value + soon.value),
    nearWindowDays,
    soonWindowDays,
  };
}

/** Bucket ordering helper for callers that want to group rather than sort by date. */
export function bucketRank(bucket: ExpiryBucket): number {
  return BUCKET_RANK[bucket];
}
